import { Chip } from "@/components/atoms/chip";
import { MediaDataTypes } from "@/components/atoms/media/MediaData";
import { StarRating } from "@/components/atoms/starRating";
import TextWithDownload, {
  TextWithDownloadProps,
} from "@/components/molecules/TextWithDownload/TextWithDownload";
import { ChipVariants, FundsTagType } from "@/types/consts";
import { renderMedia } from "@/utils/renderMediaComponent";
import {
  AspectRatio,
  Box,
  chakra,
  Grid,
  Heading,
  Text,
} from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";
import KeyPointsBlock from "../keyPointsBlock/KeyPointsBlock";
import { KeyPointsProps } from "@/components/molecules/keyPointsCard";
import { HeaderContactDetails } from "@/components/molecules/contactModal";
import { useTranslations } from "next-intl";

export interface FundsHeaderBaseProps {
  title: string;
  openForInvestment: boolean;
  tags?: {
    type: (typeof FundsTagType)[keyof typeof FundsTagType];
    value: string;
  }[];
  contactTitle?: string;
  contactSubtitle?: string;
  contactDetails?: HeaderContactDetails;
}

export interface FundsHeaderProps extends FundsHeaderBaseProps {
  intro?: string;
  mediaData?: MediaDataTypes;
  features?: {
    title: string;
    info: string | number;
  }[];
  detailPoints?: TextWithDownloadProps;
  keyPoints?: KeyPointsProps[];
}

export default chakra(FundsDetailHeader);
function FundsDetailHeader({
  title,
  intro,
  mediaData,
  openForInvestment,
  tags,
  features,
  detailPoints,
  keyPoints,
}: FundsHeaderProps) {
  const t = useTranslations();

  const media = mediaData
    ? renderMedia(mediaData, { base: "fundsHeader" }, { base: 1 })
    : undefined;

  return (
    <Box as="header" display="flex" flexDirection="column" gap={5}>
      <Heading
        as="h1"
        display={{ base: "block", lg: "none" }}
        order={-3}
        size="unset"
        textStyle="display.lg"
        fontWeight={400}
        color={"brand.aubergine"}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(title.replace(/<\/p>/g, ""), {
            ALLOWED_TAGS: ["strong"],
          }),
        }}
        sx={{
          strong: {
            display: "block",
            pt: 2,
            textStyle: "heading.xxl",
            fontWeight: 300,
            color: "brand.cyan",
          },
        }}
      />
      <Chip
        display={{ base: "flex", lg: "none" }}
        order={-1}
        mt={8}
        mb={5}
        px={0}
        ellipse
        ellipseColor={!openForInvestment ? "brand.aubergine" : undefined}
        text={
          openForInvestment ? t("investment Open") : t("investment Closed")
        }
        variant={ChipVariants.Solid}
        w="fit-content"
      />
      {intro && (
        <Text
          maxW="55rem"
          px={{ lg: 10 }}
          textStyle={{ base: "display.sm", md: "display.md" }}
          color={"brand.aubergine"}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(intro.replace(/<\/p>/g, "</br>"), {
              ALLOWED_TAGS: ["strong", "br"],
            }),
          }}
          sx={{
            strong: {
              fontWeight: 300,
              color: "brand.cyan",
              fontFamily: "Lausanne, sans-serif",
            },
          }}
        />
      )}
      <Box
        display={{ base: "flex", lg: "none" }}
        flexWrap="wrap"
        gap={2}
        my={9}
      >
        {tags
          ?.filter((tag) => tag.value)
          .map((tag) => (
            <Chip
              key={tag.value}
              variant={ChipVariants.Outline}
              text={
                tag.type === FundsTagType.MinimumInvestment
                  ? `${t("minimum Investment Label")}: ${tag.value}`
                  : tag.value
              }
            />
          ))}
      </Box>
      {features && (
        <Grid
          display={{ base: "none", md: "grid" }}
          templateColumns={{
            md: "repeat(3, 1fr)",
            "2xl": `repeat(${features.length}, 1fr)`,
          }}
          rowGap={2}
          mt={{ base: 0, lg: 12 }}
          px={{ md: 5, lg: 10 }}
          py={5}
        >
          {features.map((feature, index) => (
            //--index as key is accepted, list is static--//
            <Box
              key={index}
              py={3}
              px={10}
              borderRight="1px solid"
              borderColor="gray.200"
              _first={{ paddingLeft: 0 }}
              _last={{ border: "none" }}
              sx={{
                "&:nth-of-type(3)": {
                  borderRight: { base: "none", "2xl": "1px solid" },
                  borderColor: { "2xl": "gray.200" },
                },
                "&:nth-of-type(4)": {
                  paddingLeft: { base: 0, "2xl": 10 },
                },
              }}
            >
              <Text
                textStyle={{ base: "paragraph.sm", "2xl": "paragraph.md" }}
                fontWeight={400}
                color="aubergine.950"
              >
                {feature.title}
              </Text>
              {typeof feature.info === "number" ? (
                <StarRating rating={feature.info} />
              ) : (
                <Text textStyle="display.sm" color="brand.aubergine">
                  {feature.info}
                </Text>
              )}
            </Box>
          ))}
        </Grid>
      )}
      {media && (
        <AspectRatio
          order={{ base: -2, lg: 0 }}
          ratio={16 / 9}
          sx={{
            "& div, iframe": {
              width: "100%",
              height: "100%",
            },
          }}
        >
          {media}
        </AspectRatio>
      )}
      {(detailPoints?.text || detailPoints?.download?.url) && (
        <TextWithDownload
          text={detailPoints?.text ?? ""}
          download={detailPoints?.download}
        />
      )}
      {keyPoints && <KeyPointsBlock keyPoints={keyPoints} />}
    </Box>
  );
}
